import React, { memo, forwardRef, useImperativeHandle, useState } from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import { createHarvestSummary } from 'api/reports';
import { useMutation, useQueryClient } from 'react-query';

import {
  AddHarvestSummaryModalRefType,
  AddHarvestSummaryModalProps,
} from './AddHarvestSummaryModal.props';

import AddHarvestSummaryModalView from './AddHarvestSummaryModal.view';
import { format } from 'date-fns';
import { InventoryCount } from 'types/inventory';

const Container = forwardRef<
  AddHarvestSummaryModalRefType,
  AddHarvestSummaryModalProps
>((props, ref) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [
    selectedInventoryItem,
    setSelectedInventoryItem,
  ] = useState<InventoryCount | null>(null);

  useImperativeHandle(
    ref,
    (): any => ({
      onOpen: (count: any) => {
        setSelectedInventoryItem(count);
        onOpen();
      },
    }),
    [onOpen],
  );

  const { mutate, isLoading } = useMutation(createHarvestSummary, {
    onSuccess: () => {
      toast({
        status: 'success',
        title: 'Success',
        description: 'Harvest Summary Created',
        isClosable: true,
        duration: 5000,
      });

      queryClient.invalidateQueries({
        queryKey: ['fetch-reports'],
      });

      queryClient.invalidateQueries({
        queryKey: [`fetch-inventory-reports`],
      });

      // Invalidate all activity history queries since new reports are created
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length > 0 &&
            queryKey[0].includes('fetch-report-activity-history')
          );
        },
      });

      onClose();
    },
  });

  return (
    <AddHarvestSummaryModalView
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={mutate}
      selectedInventoryItem={selectedInventoryItem}
    />
  );
});

const AddHarvestSummaryModal = memo(Container);

export default AddHarvestSummaryModal;
